/**
 * DisposalWellsTab — Phase 24 / Task 24.3 (DEC-094).
 *
 * Lists the hauler's authorized disposal wells. Authorize flow searches the
 * platform wells catalog filtered to SWD/INJ (the server also hard-blocks
 * non-disposal well types). Row click opens the display-name / deactivate panel.
 */
import * as React from 'react';
import {
  useDisposalFacilities,
  useWellSearch,
  type DisposalFacility,
  type WellResult,
} from './hooks';
import { CatalogSearchModal } from './CatalogSearchModal';
import { DisposalFormModal } from './DisposalFormModal';
import {
  ActiveBadge,
  AddButton,
  Pill,
  SectionHeader,
  rowStyle,
  searchInputStyle,
  subText,
} from './ui-bits';

const DISPOSAL_TYPES = new Set(['SWD', 'INJ']);

export function DisposalWellsTab({ canManage }: { canManage: boolean }): React.JSX.Element {
  const { data, isLoading, isError } = useDisposalFacilities();
  const [filter, setFilter] = React.useState('');
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [formMode, setFormMode] = React.useState<'authorize' | 'edit'>('authorize');
  const [pickedWell, setPickedWell] = React.useState<WellResult | null>(null);
  const [editing, setEditing] = React.useState<DisposalFacility | null>(null);

  const facilities = data?.haulerDisposalFacilities ?? [];
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? facilities.filter(
        (f) =>
          f.resolvedName.toLowerCase().includes(needle) ||
          (f.operatorName ?? '').toLowerCase().includes(needle),
      )
    : facilities;

  // Well search constrained client-side to disposal well types (server hard-blocks too).
  const useSearch = (q: string): { items: readonly WellResult[]; isFetching: boolean } => {
    const { data: wd, isFetching } = useWellSearch(q);
    const items = (wd?.wells ?? []).filter(
      (w) => w.wellType != null && DISPOSAL_TYPES.has(w.wellType),
    );
    return { items, isFetching };
  };

  return (
    <div>
      <SectionHeader
        title="Disposal Wells"
        count={facilities.length}
        action={
          canManage ? (
            <AddButton label="Authorize Disposal Well" onClick={(): void => setSearchOpen(true)} />
          ) : null
        }
      />
      <input
        value={filter}
        onChange={(e): void => setFilter(e.target.value)}
        placeholder="Filter disposal wells…"
        style={searchInputStyle}
        aria-label="Filter disposal wells"
      />

      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load disposal wells.</p>
      )}
      {!isLoading && visible.length === 0 && (
        <p style={subText}>No authorized disposal wells yet.</p>
      )}

      {visible.map((f) => (
        <div
          key={f.id}
          style={{ ...rowStyle, cursor: 'pointer', opacity: f.isActive ? 1 : 0.55 }}
          onClick={(): void => {
            setEditing(f);
            setPickedWell(null);
            setFormMode('edit');
            setFormOpen(true);
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 600 }}>{f.resolvedName}</span>
              {f.wellType != null && <Pill label={f.wellType} tone="accent" />}
            </div>
            <div style={subText}>
              {f.operatorName ?? 'Unknown operator'}
              {f.county != null ? ` · ${f.county}` : ''} · {f.state} · API {f.apiNumber}
            </div>
          </div>
          <ActiveBadge active={f.isActive} />
        </div>
      ))}

      <CatalogSearchModal<WellResult>
        open={searchOpen}
        onOpenChange={setSearchOpen}
        title="Authorize disposal well"
        placeholder="Search disposal wells (SWD/INJ)…"
        useSearch={useSearch}
        itemKey={(w): string => w.id}
        renderItem={(w): React.ReactNode => (
          <span>
            <span style={{ fontWeight: 600 }}>{w.wellName}</span>
            <span style={subText}>
              {w.operatorName != null ? ` · ${w.operatorName}` : ''}
              {w.county != null ? ` · ${w.county}` : ''} · {w.state} · {w.wellType}
            </span>
          </span>
        )}
        onPick={(w): void => {
          setSearchOpen(false);
          setPickedWell(w);
          setEditing(null);
          setFormMode('authorize');
          setFormOpen(true);
        }}
      />

      <DisposalFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        well={pickedWell}
        facility={editing}
        canManage={canManage}
      />
    </div>
  );
}
